import React from 'react';
import styles from './SocialShareLinkBox.scss';
import { useBasePageQuery, useOptimizely, usePageMeta } from '../../hooks';
import { Experiments } from '../../constants/experiments';
import { TikTokIcon } from '../SocialIcons/TikTokIcon';
import { YoutubeIcon } from '../SocialIcons/YoutubeIcon';
import { SlackIcon } from '../SocialIcons/SlackIcon';
import { TeamsIcon } from '../SocialIcons/TeamsIcon';
import { LinktreeIcon } from '../SocialIcons/LinktreeIcon';
import { SnapchatIcon } from '../SocialIcons/SnapchatIcon';
import { RedditIcon } from '../SocialIcons/RedditIcon';
import { TwitchIcon } from '../SocialIcons/TwitchIcon';
import { SocialsIconsShareWrapper } from '../SocialIcons/SocialIconsShareWrapper';
import { useTranslation } from 'react-i18next';
import { useCharity } from '../../hooks/page/useCharity';
import { useProduct } from '../../hooks/page/product/useProduct';
import { OwnerType } from '../../../types/__generated__';
import PAGE_TYPES from '../../constants/pageTypes';

function useProductOwner() {
  const { pageType } = usePageMeta();
  const { name: charityName } = useCharity();
  const {
    data: { ownerType },
  } = useProduct();
  const { data } = useBasePageQuery();

  switch (true) {
    case ownerType === OwnerType.Organisation:
      return charityName;
    case pageType === PAGE_TYPES.FUNDRAISING ||
      ownerType === OwnerType.Individual:
    default:
      return data?.page?.owner?.name;
  }
}

export interface SocialShareLinkBoxProps {
  onShareItemClick?: () => void;
}

export const SocialShareLinkBox: React.FunctionComponent<SocialShareLinkBoxProps> = ({
  onShareItemClick,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });

  const optimizely = useOptimizely();
  const productOwner = useProductOwner();

  function onShare() {
    if (optimizely) {
      optimizely.track(Experiments.TrackingEvents.shareSocialButtonClick);
    }

    if (onShareItemClick) {
      onShareItemClick();
    }
  }

  return (
    <div className={styles.container}>
      <p className={styles.shareBoxTitle}>
        {t('fundraising.SocialShareLinkBoxTitle', {
          name: productOwner,
        })}
      </p>
      <div>
        <ul className={styles.socialIconList} data-qa="SocialIconShareList">
          <li className={styles.listItem} data-qa="SocialIconShareListItem">
            <SocialsIconsShareWrapper
              children={<TikTokIcon />}
              socialChannel={'tiktok'}
              onClick={onShare}
            />
          </li>{' '}
          <li className={styles.listItem} data-qa="SocialIconShareListItem">
            <SocialsIconsShareWrapper
              children={<YoutubeIcon />}
              socialChannel={'youtube'}
              onClick={onShare}
            />
          </li>{' '}
          <li className={styles.listItem} data-qa="SocialIconShareListItem">
            <SocialsIconsShareWrapper
              children={<SlackIcon />}
              socialChannel={'slack'}
              onClick={onShare}
            />
          </li>{' '}
          <li className={styles.listItem} data-qa="SocialIconShareListItem">
            <SocialsIconsShareWrapper
              children={<TeamsIcon />}
              socialChannel={'teams'}
              onClick={onShare}
            />
          </li>{' '}
          <li className={styles.listItem} data-qa="SocialIconShareListItem">
            <SocialsIconsShareWrapper
              children={<LinktreeIcon />}
              socialChannel={'linktree'}
              onClick={onShare}
            />
          </li>{' '}
          <li className={styles.listItem} data-qa="SocialIconShareListItem">
            <SocialsIconsShareWrapper
              children={<SnapchatIcon />}
              socialChannel={'snapchat'}
              onClick={onShare}
            />
          </li>
          <li className={styles.listItem} data-qa="SocialIconShareListItem">
            <SocialsIconsShareWrapper
              children={<RedditIcon />}
              socialChannel={'reddit'}
              onClick={onShare}
            />
          </li>
          <li className={styles.listItem} data-qa="SocialIconShareListItem">
            <SocialsIconsShareWrapper
              children={<TwitchIcon />}
              socialChannel={'twitch'}
              onClick={onShare}
            />
          </li>
        </ul>
      </div>
    </div>
  );
};
